import React, { useCallback } from "react";
import ModalWrapper from "..";
import { Button } from "../../button";
import { useTranslation } from "react-i18next";
import toast, { Toaster } from "react-hot-toast";
import { useCancelAppointmentById } from "../../../hooks/visitor/mutation";
import { API_ERRORS } from "../../../constants/common";

interface CancelAppointmentModalProps {
  selectedAppointmentId: string;
  setSelectedAppointmentId: React.Dispatch<React.SetStateAction<string>>;
  isOpen: boolean;
  onOpenChange: () => void;
}

const CancelAppointmentModal = ({
  selectedAppointmentId,
  setSelectedAppointmentId,
  isOpen,
  onOpenChange,
}: CancelAppointmentModalProps) => {
  const {
    mutateAsync: CancelAppointmentById,
    isPending: isCancelAppointmentByIdLoading,
  } = useCancelAppointmentById();

  const { t } = useTranslation();

  const handleCancelAppointment = useCallback(async () => {
    try {
      if (selectedAppointmentId !== "0") {
        const body = {
          encryptedId: selectedAppointmentId,
        };

        await CancelAppointmentById(body);

        toast.success(t("appointmentCancelledSuccessfully"));
        onOpenChange();
        setSelectedAppointmentId("0");
      }
    } catch (error: any) {
      const errorCode = Object.keys(error.errors)[0];

      if (errorCode === API_ERRORS.CANNOT_CANCEL_OLD_VISIT_REQUESTS) {
        toast.error(t("cannotCancelOldVisitRequests"));
      } else if (errorCode === API_ERRORS.VISIT_ALREADY_CANCELLED) {
        toast.error(t("visitAlreadyCancelled"));
      } else {
        toast.error(t("somethingWentWrong"));
      }
    }
  }, [
    selectedAppointmentId,
    CancelAppointmentById,
    t,
    onOpenChange,
    setSelectedAppointmentId,
  ]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <ModalWrapper isOpen={isOpen} onOpenChange={onOpenChange}>
        <div className="flex flex-col gap-6 justify-center items-center">
          <p className="text-lg md:text-xl font-medium">
            {t("sureToCancelAppointment")}
          </p>

          <div className="flex items-center gap-3">
            <Button
              isLoading={isCancelAppointmentByIdLoading}
              onClick={handleCancelAppointment}
            >
              {t("yesCancel")}
            </Button>
            <Button
              onClick={() => onOpenChange()}
              className="bg-transparent border-2 border-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("noClose")}
            </Button>
          </div>
        </div>
      </ModalWrapper>
    </>
  );
};

export default React.memo(CancelAppointmentModal);
